import { useQueries, useQuery } from "@tanstack/react-query";
import {
  getOrdersCountByStoreId,
  getOrdersAverageByStoreId,
  getTotalRevenue,
  getRevenueForPeriod,
  getOrdersStatusAverage,
  getActiveOrders,
  getMerchantOrderHistory,
} from "../../webService/webService";
import { queryKeys } from "../queryKeys";

/**
 * The five merchant dashboard metrics for a given storeId/period, fetched as
 * one `useQueries` fan-out (mirrors the dashboard's original
 * `Promise.all([...])`) so each metric gets its own cache entry — switching
 * back to a previously-viewed period is instant — while still exposing one
 * combined loading/error flag for the page-level skeleton.
 */
export const useStoreMetrics = (storeId, period, token) => {
  const results = useQueries({
    queries: [
      {
        queryKey: queryKeys.merchant.ordersCount(storeId, period),
        queryFn: () => getOrdersCountByStoreId(storeId, period, token),
        enabled: Boolean(storeId && period && token),
      },
      {
        queryKey: queryKeys.merchant.ordersAverage(storeId, period),
        queryFn: () => getOrdersAverageByStoreId(storeId, period, token),
        enabled: Boolean(storeId && period && token),
      },
      {
        queryKey: queryKeys.merchant.revenue(storeId, period),
        queryFn: () => getTotalRevenue(storeId, period, token),
        enabled: Boolean(storeId && period && token),
      },
      {
        queryKey: queryKeys.merchant.revenuePeriod(storeId, period),
        queryFn: () => getRevenueForPeriod(storeId, period, token),
        enabled: Boolean(storeId && period && token),
      },
      {
        queryKey: queryKeys.merchant.statusAverage(storeId, period),
        queryFn: () => getOrdersStatusAverage(storeId, period, token),
        enabled: Boolean(storeId && period && token),
      },
    ],
  });

  const [ordersCount, ordersAverage, revenue, revenuePeriod, statusAverage] = results;

  return {
    ordersCount: ordersCount.data,
    ordersAverage: ordersAverage.data,
    revenue: revenue.data,
    revenuePeriod: revenuePeriod.data,
    statusAverage: statusAverage.data,
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    error: results.find((r) => r.error)?.error,
  };
};

export const useActiveOrdersByStore = (storeId, token, options = {}) =>
  useQuery({
    queryKey: queryKeys.merchant.activeOrders(storeId),
    queryFn: () => getActiveOrders(storeId, token),
    enabled: Boolean(storeId && token),
    staleTime: 10_000,
    ...options,
  });

export const useMerchantOrderHistory = (storeId, filter, token, options = {}) =>
  useQuery({
    queryKey: queryKeys.merchant.orderHistory(storeId, filter),
    queryFn: () => getMerchantOrderHistory(storeId, filter, token),
    enabled: Boolean(storeId && filter && token),
    ...options,
  });
